import React, { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import { api } from '../../services/api';
import FilterCell from '../Common/FilterCell';
import {
  EMPTY_VALIDATION,
  validateExcelStructure,
  validateBoqData,
  validateBoqServerEdit,
  buildServerBoqSnapshot,
  isBoqStatusEditable,
  getBoqStatusDisplayLabel,
  getBoqStatusDisplayVariant,
  isPieceUnit,
} from './boqValidation';
import './Reports.css';
import './BoqReport.css';

const normalizeMigvanList = (items) =>
  (items || [])
    .map((item) => ({
      code: String(item.code ?? item.Code ?? item['קוד מגוון'] ?? item['קוד'] ?? '').trim(),
      name: String(
        item.name ?? item.Name ?? item.description ?? item.Description ?? item['מגוון'] ?? item['תיאור פריט'] ?? ''
      ).trim(),
      unit: String(item.unit ?? item.Unit ?? item['י.מידה'] ?? item['יחידה'] ?? '').trim(),
    }))
    .filter((item) => item.code || item.name);

const normalizeBuildingList = (buildings) =>
  (buildings || []).map((building, index) => {
    const id = building.id ?? building.buildingId ?? building.BuildingId ?? index;
    const number = String(
      building.number ?? building.Number ?? building['מספר בניין'] ?? building['מספר'] ?? ''
    ).replace(/[^0-9]/g, '');

    return {
      ...building,
      id,
      number,
      name: building.name ?? building.Name ?? building['שם בניין'] ?? building['תאור'] ?? '',
      numFlrs: Math.floor(Number(building.numFlrs ?? building.NumFlrs ?? 0)) || 0,
      numAprts: Math.floor(Number(building.numAprts ?? building.NumAprts ?? 0)) || 0,
    };
  });

const getBuildingCandidateKeys = (building, buildingId = building?.id) => {
  const keys = new Set();
  if (buildingId !== undefined && buildingId !== null && buildingId !== '') {
    keys.add(String(buildingId));
  }
  if (building?.id !== undefined && building?.id !== null && building?.id !== '') {
    keys.add(String(building.id));
  }
  if (building?.number) {
    keys.add(String(building.number).trim());
  }
  return [...keys].filter(Boolean);
};

const resolveBoqItemsForBuilding = (boqItems, buildings, buildingId) => {
  if (!boqItems || typeof boqItems !== 'object') return [];

  const building = buildings.find(
    (b) => b.id === buildingId
      || String(b.id) === String(buildingId)
      || Number(b.id) === Number(buildingId)
  );

  const candidateKeys = building
    ? getBuildingCandidateKeys(building, buildingId)
    : [String(buildingId)];

  let matchedItems = null;
  for (const key of candidateKeys) {
    const items = boqItems[key];
    if (Array.isArray(items)) {
      matchedItems = items;
      if (items.length > 0) break;
    }
  }

  if (matchedItems) return matchedItems;

  for (const [key, items] of Object.entries(boqItems)) {
    if (!Array.isArray(items)) continue;
    if (candidateKeys.includes(String(key))) return items;
  }

  return [];
};

const collectAllBoqItems = (boqItems, buildings) =>
  buildings.flatMap((building) =>
    resolveBoqItemsForBuilding(boqItems, buildings, building.id).map((item) => ({
      ...item,
      buildingId: building.id,
    }))
  );

const normalizeMigvanCode = (code) => String(code ?? '').trim();

const parseBoqQuantity = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const sumQuantityByMigvanCode = (items, code) => {
  const normalizedCode = normalizeMigvanCode(code);
  if (!normalizedCode) return 0;

  return (items || []).reduce((sum, item) => {
    if (normalizeMigvanCode(item.code) !== normalizedCode) return sum;
    return sum + parseBoqQuantity(item.quantity);
  }, 0);
};

const formatBoqQuantity = (value, unit) => {
  if (isPieceUnit(unit)) return String(Math.round(value));
  return value.toLocaleString('he-IL', { maximumFractionDigits: 2 });
};

const getMigvanMeta = (code, migvan, item) => {
  const normalizedCode = normalizeMigvanCode(code);
  const match = (migvan || []).find((entry) => normalizeMigvanCode(entry.code) === normalizedCode);

  return {
    code: normalizedCode,
    description: match?.name || item?.name || '',
    unit: match?.unit || item?.unit || '',
  };
};

const buildMigvanTooltipInfo = ({
  code,
  item,
  migvan,
  allBoqItems,
  itemBuildingId,
}) => {
  const meta = getMigvanMeta(code, migvan, item);
  if (!meta.code || !allBoqItems?.length) return null;

  const buildingItems = itemBuildingId != null
    ? allBoqItems.filter((entry) => String(entry.buildingId) === String(itemBuildingId))
    : allBoqItems;

  const currentBuildingQty = sumQuantityByMigvanCode(buildingItems, meta.code);
  const allBuildingsQty = sumQuantityByMigvanCode(allBoqItems, meta.code);

  return {
    ...meta,
    currentBuildingQty,
    allBuildingsQty,
    currentBuildingQtyLabel: formatBoqQuantity(currentBuildingQty, meta.unit),
    allBuildingsQtyLabel: formatBoqQuantity(allBuildingsQty, meta.unit),
  };
};

const assignUniqueBoqItemIds = (items) => {
  const seenIds = new Set();
  let nextId = 0;

  return items.map((item) => {
    const rawId = item.id ?? item.Id ?? item.itemId;
    let id = rawId;

    if (id !== undefined && id !== null && id !== '') {
      const numericId = Number(id);
      id = Number.isNaN(numericId) ? String(id) : numericId;
    } else {
      id = null;
    }

    if (id === null || seenIds.has(id)) {
      while (seenIds.has(nextId)) {
        nextId += 1;
      }
      id = nextId;
      nextId += 1;
    } else if (typeof id === 'number') {
      nextId = Math.max(nextId, id + 1);
    }

    seenIds.add(id);
    return { ...item, id };
  });
};

const normalizeBoqItemsMap = (buildings, rawBoqItems, mapItem = (item) => item) => {
  const normalized = {};
  buildings.forEach((building) => {
    const items = resolveBoqItemsForBuilding(rawBoqItems, buildings, building.id);
    normalized[String(building.id)] = assignUniqueBoqItemIds(
      items.map((item) => mapItem(item, building.id))
    );
  });
  return normalized;
};

const PARTIAL_TENANT_FIELDS = new Set([
  'name', 'family', 'id', 'phone1', 'phone2', 'email', 'note',
  'AlwEmail', 'U_AlwSms', 'U_AlwWApp',
]);

const normalizeTenantsFromServer = (serverTenants) =>
  (serverTenants || []).map((t, index) => ({
    ...t,
    tenantId: t.tenantId !== undefined ? t.tenantId : index,
    id: t.id || t.idNumber || '',
    isNew: false,
    AlwEmail: t.AlwEmail !== undefined ? t.AlwEmail : (t.emailApproval !== undefined ? t.emailApproval : false),
    U_AlwSms: t.U_AlwSms !== undefined ? t.U_AlwSms : (t.AlwSms !== undefined ? t.AlwSms : (t.smsApproval !== undefined ? t.smsApproval : false)),
    U_AlwWApp: t.U_AlwWApp !== undefined ? t.U_AlwWApp : (t.AlwWApp !== undefined ? t.AlwWApp : (t.whatsappApproval !== undefined ? t.whatsappApproval : false)),
  }));

export default function ExcelUpload({ user, selectedContractId, unsavedChangesRef, onSummaryChange }) {
  // מבנה נתונים
  const [buildings, setBuildings] = useState([]); // גיליון 1: רשימת בניינים
  const [tenants, setTenants] = useState([]); // גיליון 2: רשימת דיירים
  const [boqItems, setBoqItems] = useState({}); // גיליונות 3+: {buildingId: [items]}
  const [migvan, setMigvan] = useState([]); // רשימת מגוונים מ-migvan: [{code, name, unit}]
  
  const [selectedBuildingId, setSelectedBuildingId] = useState(null);
  const [activeTab, setActiveTab] = useState('buildings'); // 'buildings', 'tenants', 'boq'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [hasNoData, setHasNoData] = useState(false);
  const [isFromServer, setIsFromServer] = useState(false); // האם הנתונים מהשרת
  const [isEditing, setIsEditing] = useState(false); // מצב עריכה (העלאה / הזנה ידנית)
  const [boqStatus, setBoqStatus] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [serverBoqSnapshot, setServerBoqSnapshot] = useState({});
  const messageTimeoutRef = useRef(null);
  const fileUploadBoqRef = useRef(null);
  const fileUploadBoqExistingRef = useRef(null);

  const isServerEditable = isFromServer && isBoqStatusEditable(boqStatus) && !hasNoData;
  const isFullEditable = !isFromServer && isEditing;
  const tenantsEditMode = isFullEditable ? 'full' : (isServerEditable ? 'partial' : 'none');
  const boqEditMode = isFullEditable ? 'full' : (isServerEditable ? 'quantities' : 'none');
  const showSaveButton = (isFullEditable || (isServerEditable && isDirty)) && !uploading;

  const markDirty = useCallback(() => {
    setIsDirty(true);
    if (!isFromServer) {
      setIsEditing(true);
    }
  }, [isFromServer]);

  const validation = useMemo(() => {
    if (isFullEditable) {
      return validateBoqData({ buildings, tenants, boqItems, migvan });
    }
    if (isServerEditable && isDirty) {
      return validateBoqServerEdit({ buildings, tenants, boqItems, serverBoqSnapshot });
    }
    return EMPTY_VALIDATION;
  }, [buildings, tenants, boqItems, migvan, isFullEditable, isServerEditable, isDirty, serverBoqSnapshot]);

  const allBoqItemsFlat = useMemo(
    () => collectAllBoqItems(boqItems, buildings),
    [boqItems, buildings]
  );

  useEffect(() => {
    if (!unsavedChangesRef) return undefined;

    unsavedChangesRef.current = isDirty && (isFullEditable || isServerEditable);
    return () => {
      unsavedChangesRef.current = false;
    };
  }, [isDirty, isFullEditable, isServerEditable, unsavedChangesRef]);

  useEffect(() => {
    if (!onSummaryChange) return undefined;
    onSummaryChange({
      buildings: buildings.length,
      tenants: tenants.length,
      statusLabel: getBoqStatusDisplayLabel(boqStatus),
      statusVariant: getBoqStatusDisplayVariant(boqStatus),
    });
    return () => onSummaryChange(null);
  }, [buildings.length, tenants.length, boqStatus, onSummaryChange]);

  // בחר בניין ראשון אוטומטית כשעוברים ללשונית כתב כמויות
  useEffect(() => {
    if (activeTab === 'boq' && selectedBuildingId === null && buildings.length > 0) {
      setSelectedBuildingId(buildings[0].id);
    }
  }, [activeTab, buildings, selectedBuildingId]);

  // טען נתונים מ-boq API
  const applyBoqResponse = useCallback((response) => {
    const status = response?.status ?? null;
    setBoqStatus(status);

    const serverBuildings = Array.isArray(response?.buildings) ? response.buildings : [];
    const serverTenants = Array.isArray(response?.tenants) ? response.tenants : [];
    const serverBoqItems = response?.boqItems && typeof response.boqItems === 'object' ? response.boqItems : {};
    const serverMigvan = Array.isArray(response?.migvan) ? response.migvan : [];

    const hasBoqItems = Object.keys(serverBoqItems).length > 0
      && Object.values(serverBoqItems).some((items) => Array.isArray(items) && items.length > 0);

    if (serverBuildings.length > 0 || serverTenants.length > 0 || hasBoqItems) {
      const normalizedBuildings = normalizeBuildingList(serverBuildings);
      const normalizedBoqItems = normalizeBoqItemsMap(
        normalizedBuildings,
        serverBoqItems,
        (item, buildingId) => ({
          ...item,
          buildingId: item.buildingId !== undefined ? item.buildingId : buildingId,
          name: item.name || '',
          code: item.code || '',
          room: item.room || '',
          aprt: item.aprt || '',
          unit: item.unit || '',
          quantity: item.quantity || 0,
          wstgPrcnt: item.wstgPrcnt !== undefined ? item.wstgPrcnt : 0,
          bufferQty: item.bufferQty !== undefined ? item.bufferQty : 0,
          shipDate: normalizeShipDate(item.shipDate),
        })
      );

      setBuildings(normalizedBuildings);
      setTenants(normalizeTenantsFromServer(serverTenants));
      setBoqItems(normalizedBoqItems);
      setServerBoqSnapshot(buildServerBoqSnapshot(normalizedBuildings, normalizedBoqItems));
      setMigvan(normalizeMigvanList(serverMigvan));
      setIsFromServer(true);
      setIsEditing(false);
      setIsDirty(false);
      setHasNoData(false);

      if (normalizedBuildings.length > 0) {
        setSelectedBuildingId(normalizedBuildings[0].id);
      }
      return true;
    }

    setBuildings([]);
    setTenants([]);
    setBoqItems({});
    setServerBoqSnapshot({});
    setMigvan(normalizeMigvanList(serverMigvan));
    setIsFromServer(false);
    setIsEditing(false);
    setIsDirty(false);
    setHasNoData(true);
    return false;
  }, []);

  useEffect(() => {
    if (!selectedContractId || !user) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setHasNoData(false);

        const response = await api.getBoqReport(
          user.userCode,
          user.type,
          selectedContractId
        );

        if (response && (response.buildings || response.tenants || response.boqItems || response.migvan)) {
          applyBoqResponse(response);
        } else {
          setBoqStatus(response?.status ?? null);
          setBuildings([]);
          setTenants([]);
          setBoqItems({});
          setServerBoqSnapshot({});
          setMigvan([]);
          setIsFromServer(false);
          setIsEditing(false);
          setIsDirty(false);
          setHasNoData(true);
        }
      } catch (err) {
        console.error('Error fetching BOQ data:', err);
        setError(err.message || 'שגיאה בטעינת הנתונים מהשרת');
        setBuildings([]);
        setTenants([]);
        setBoqItems({});
        setServerBoqSnapshot({});
        setIsFromServer(false);
        setIsEditing(false);
        setIsDirty(false);
        setHasNoData(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedContractId, user, applyBoqResponse]);

  const hasExistingBoqData = () => {
    if (buildings.length > 0 || tenants.length > 0) return true;
    return Object.values(boqItems).some(
      (items) => Array.isArray(items) && items.length > 0
    );
  };

  const handleUploadClick = (inputRef) => {
    if (!inputRef?.current || uploading) return;

    if (hasExistingBoqData()) {
      const proceed = window.confirm(
        'כבר הוזנו נתונים. האם לדרוס את הנתונים הקיימים ולטעון את הקובץ החדש?'
      );
      if (!proceed) return;
    }

    inputRef.current.value = '';
    inputRef.current.click();
  };

  // קריאת קובץ אקסל
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      // נקה timeout קודם אם יש
      if (messageTimeoutRef.current) {
        clearTimeout(messageTimeoutRef.current);
      }
      setMessage('שגיאה: נא לבחור קובץ אקסל (.xlsx או .xls)');
      messageTimeoutRef.current = setTimeout(() => {
        setMessage('');
        messageTimeoutRef.current = null;
      }, 10000);
      return;
    }

    setSelectedFile(file);
    setMessage('');
    setLoading(true);
    
    try {
      const workbook = await readExcelFile(file);

      const structureValidation = validateExcelStructure(workbook);
      if (structureValidation.hasBlockingErrors) {
        throw new Error(structureValidation.errors.join('\n'));
      }

      const parsed = parseExcelData(workbook);
      
      // וודא שיש לפחות בניין אחד
      if (parsed.buildings.length === 0) {
        throw new Error('הקובץ חייב להכיל לפחות בניין אחד בגיליון הראשון');
      }
      
      // בדוק קודי מגוון מול migvan
      if (migvan && migvan.length > 0) {
        Object.keys(parsed.boqItems).forEach(buildingKey => {
          parsed.boqItems[buildingKey] = parsed.boqItems[buildingKey].map(item => {
            // בדוק אם הקוד תואם ל-migvan
            const codeMatches = migvan.some(m => m.code === item.code);
            return {
              ...item,
              invalidCode: !codeMatches && item.code && item.code.trim() !== ''
            };
          });
        });
      }
      
      setBuildings(parsed.buildings);
      setTenants(parsed.tenants);
      setBoqItems(parsed.boqItems);
      setServerBoqSnapshot({});
      setBoqStatus(null);
      setIsFromServer(false);
      setIsEditing(true);
      setIsDirty(true);
      setHasNoData(false);
      
      if (parsed.buildings.length > 0) {
        setSelectedBuildingId(parsed.buildings[0].id || 0);
      }

      const dataValidation = validateBoqData({
        buildings: parsed.buildings,
        tenants: parsed.tenants,
        boqItems: parsed.boqItems,
        migvan,
      });
      
      // נקה timeout קודם אם יש
      if (messageTimeoutRef.current) {
        clearTimeout(messageTimeoutRef.current);
      }

      if (dataValidation.errorCount > 0) {
        setMessage(`הקובץ נטען. נמצאו ${dataValidation.errorCount} שגיאות – שורות לא תקינות מסומנות באדום.`);
        messageTimeoutRef.current = setTimeout(() => {
          setMessage('');
          messageTimeoutRef.current = null;
        }, 15000);
      } else if (dataValidation.warningCount > 0) {
        setMessage(`הקובץ נטען בהצלחה. יש ${dataValidation.warningCount} התראות – בדקו לפני שמירה.`);
        messageTimeoutRef.current = setTimeout(() => {
          setMessage('');
          messageTimeoutRef.current = null;
        }, 12000);
      } else {
        setMessage('הקובץ נקרא בהצלחה! אנא בדקו את הנתונים לפני שמירה.');
        messageTimeoutRef.current = setTimeout(() => {
          setMessage('');
          messageTimeoutRef.current = null;
        }, 10000);
      }
    } catch (err) {
      console.error('Error reading file:', err);
      
      // נקה timeout קודם אם יש
      if (messageTimeoutRef.current) {
        clearTimeout(messageTimeoutRef.current);
      }
      
      setMessage('שגיאה בקריאת הקובץ: ' + err.message);
      
      // הסתר את ההודעה אחרי 15 שניות
      messageTimeoutRef.current = setTimeout(() => {
        setMessage('');
        messageTimeoutRef.current = null;
      }, 15000);
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  // קריאת קובץ אקסל
  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          resolve(workbook);
        } catch (err) {
          reject(err);
        }
      };
      
      reader.onerror = () => reject(new Error('שגיאה בקריאת הקובץ'));
      reader.readAsArrayBuffer(file);
    });
  };

  // פרסור נתוני אקסל למבנה המקומי
  const parseExcelData = (workbook) => {
    const buildings = [];
    const tenants = [];
    const boqItems = {};
    
    const sheetNames = workbook.SheetNames;
    
    // גיליון 1: רשימת בניינים
    if (sheetNames.length > 0) {
      const buildingsSheet = workbook.Sheets[sheetNames[0]];
      const buildingsData = XLSX.utils.sheet_to_json(buildingsSheet);
      
      buildingsData.forEach((row, index) => {
        // המר למספר שלם
        const numFlrs = row['מספר קומות'] || row['numFlrs'] || row['NumFlrs'] || 0;
        const numAprts = row['מספר דירות'] || row['numAprts'] || row['NumAprts'] || 0;
        
        // וודא שמספר בניין הוא מספרי בלבד
        const buildingNumber = row['מספר בניין'] || row['מזהה בניין'] || row['מספר'] || row['Number'] || row['number'] || '';
        const numericBuildingNumber = String(buildingNumber).replace(/[^0-9]/g, '');
        
        buildings.push({
          id: index,
          name: row['תאור'] || row['שם בניין'] || row['בניין'] || row['Name'] || row['Building'] || row['name'] || '',
          number: numericBuildingNumber,
          numFlrs: Math.floor(Number(numFlrs)) || 0,
          numAprts: Math.floor(Number(numAprts)) || 0,
          ...row
        });
      });
    }
    
    // גיליון 2: רשימת דיירים
    if (sheetNames.length > 1) {
      const tenantsSheet = workbook.Sheets[sheetNames[1]];
      const tenantsData = XLSX.utils.sheet_to_json(tenantsSheet);
      
      tenantsData.forEach((row, index) => {
        tenants.push({
          tenantId: index,
          name: row['שם פרטי'] || row['שם דייר'] || row['שם'] || row['Name'] || row['name'] || '',
          family: row['משפחה'] || row['Family'] || row['family'] || '',
          id: (() => {
            const idValue = row['תז'] || row['ID'] || row['id'] || row['IdNumber'] || '';
            // וודא שתז הוא מספרי בלבד
            return String(idValue).replace(/[^0-9]/g, '');
          })(),
          phone1: row['טלפון 1'] || row['טלפון'] || row['Phone'] || row['Phone1'] || row['phone1'] || '',
          phone2: row['טלפון 2'] || row['Phone2'] || row['Phone 2'] || row['phone2'] || '',
          email: row['מייל'] || row['אימייל'] || row['Email'] || row['email'] || '',
          note: row['הערות'] || row['Notes'] || row['note'] || '',
          building: (() => {
            const buildingValue = row['בניין'] || row['Building'] || row['building'] || '';
            // וודא שהבניין הוא מספרי בלבד (מספר בניין)
            return String(buildingValue).replace(/[^0-9]/g, '');
          })(),
          floor: (() => {
            const floorValue = row['קומה'] || row['Floor'] || row['floor'] || '';
            // וודא שקומה היא מספרית בלבד
            return String(floorValue).replace(/[^0-9]/g, '');
          })(),
          apartment: (() => {
            const aprtValue = row['דירה'] || row['Apartment'] || row['apartment'] || '';
            // וודא שדירה היא מספרית בלבד
            return String(aprtValue).replace(/[^0-9]/g, '');
          })(),
          appType: row['טיפוס'] || row['טיפוס דירה'] || row['ApartmentType'] || row['appType'] || row['AppType'] || '',
          AlwEmail: row['אישור שליחה במייל'] === true || row['אישור שליחה במייל'] === 'כן' || row['מורשה שליחה במייל'] === true || row['מורשה שליחה במייל'] === 'כן' || row['AlwEmail'] === true || row['EmailApproval'] === true || false,
          U_AlwSms: row['אישור שליחה בהודעה'] === true || row['אישור שליחה בהודעה'] === 'כן' || row['מורשה שליחת הודעה'] === true || row['מורשה שליחת הודעה'] === 'כן' || row['U_AlwSms'] === true || row['AlwSms'] === true || row['SmsApproval'] === true || false,
          U_AlwWApp: row['אישור שליחה בוואצאפ'] === true || row['אישור שליחה בוואצאפ'] === 'כן' || row['מורשה שליחה בוואצאפ'] === true || row['מורשה שליחה בוואצאפ'] === 'כן' || row['U_AlwWApp'] === true || row['AlwWApp'] === true || row['WhatsappApproval'] === true || false,
          ...row
        });
      });
    }
    
    // גיליונות 3+: כתב כמויות לפי בניין
    // כל גיליון מתאים לבניין לפי הסדר (גיליון 3 = בניין 0, גיליון 4 = בניין 1, וכו')
    for (let i = 2; i < sheetNames.length; i++) {
      const sheet = workbook.Sheets[sheetNames[i]];
      const sheetData = XLSX.utils.sheet_to_json(sheet);
      const buildingIndex = i - 2; // 0, 1, 2... (מתאים לאינדקס בבניינים)
      
      // מצא את הבניין המתאים לפי האינדקס
      const building = buildings[buildingIndex];
      if (!building) {
        // אם אין בניין מתאים, דלג על הגיליון הזה
        console.warn(`גיליון ${i + 1} (${sheetNames[i]}) לא מתאים לבניין - דילוג`);
        continue;
      }
      
      const buildingId = building.id;
      const key = String(buildingId); // המפתח כמחרוזת
      
      // אם אין עדיין רשימה לזה בניין, צור אחת
      if (!boqItems[key]) {
        boqItems[key] = [];
      }
      
      // הוסף את הפריטים מהגיליון
      const itemsFromSheet = sheetData.map((row, index) => {
        // מצא את ה-ID הבא (המקסימלי + 1)
        const existingIds = boqItems[key].map(item => item.id);
        const maxId = existingIds.length > 0 ? Math.max(...existingIds) : -1;
        const newId = maxId + index + 1;
        
        return {
          id: newId,
          buildingId: buildingId,
          aprt: String(row['דירה'] || row['aprt'] || row['Aprt'] || row['Apartment'] || '').trim(),
          name: row['מגוון'] || row['שם פריט'] || row['פריט'] || row['Name'] || row['Item'] || row['name'] || '',
          code: row['קוד מגוון'] || row['קוד'] || row['Code'] || row['code'] || '',
          room: row['חדר'] || row['שם חדר'] || row['Room'] || row['room'] || '',        
          unit: row['י.מידה'] || row['יחידה'] || row['Unit'] || row['unit'] || '',
          quantity: Number(row['כמות לפני העמסה'] || row['כמות'] || row['Quantity'] || row['quantity'] || 0),
          wstgPrcnt: Number(row['אחוז פחת'] || row['wstgPrcnt'] || row['WstgPrcnt'] || 0),
          bufferQty: Number(row['כמות לבדק'] || row['bufferQty'] || row['BufferQty'] || 0),
          shipDate: normalizeShipDate(
            row['ת.אספקה משוער'] || row['תאריך אספקה משוער'] || row['תאריך אספקה'] || row['shipDate'] || row['ShipDate'] || ''
          ),
          invalidCode: false, // יוגדר מאוחר יותר אם הקוד לא תואם
          ...row
        };
      });
      
      // הוסף את הפריטים לרשימה
      boqItems[key] = [...boqItems[key], ...itemsFromSheet];
    }
    
    return { buildings, tenants, boqItems };
  };

  // שמירה לשרת
  const handleSave = async () => {
    if (messageTimeoutRef.current) {
      clearTimeout(messageTimeoutRef.current);
    }

    if (validation.hasBlockingErrors) {
      setMessage(`לא ניתן לשמור – נמצאו ${validation.errorCount} שגיאות. תקנו שורות מסומנות באדום.`);
      messageTimeoutRef.current = setTimeout(() => {
        setMessage('');
        messageTimeoutRef.current = null;
      }, 10000);
      return;
    }

    if (validation.warnings.length > 0) {
      const preview = validation.warnings.slice(0, 5).join('\n');
      const suffix = validation.warnings.length > 5
        ? `\n... ועוד ${validation.warnings.length - 5} התראות`
        : '';
      const proceed = window.confirm(
        `נמצאו ${validation.warnings.length} התראות:\n${preview}${suffix}\n\nהאם להמשיך לשמירה?`
      );
      if (!proceed) return;
    }

    setUploading(true);
    setMessage('');
    
    try {
      // כאן תהיה שליחה לשרת
      const dataToSend = {
        buildings,
        tenants,
        boqItems,
        contractId: selectedContractId
      };
      
      // קריאה ל-API לשמירה
      await api.saveBoqReport(user.userCode, user.type, selectedContractId, dataToSend);

      setMessage('הנתונים נשמרו בהצלחה!');
      setIsDirty(false);
      setIsEditing(false);

      const refreshed = await api.getBoqReport(user.userCode, user.type, selectedContractId);
      applyBoqResponse(refreshed);
      
      // הסתר את ההודעה אחרי 10 שניות
      messageTimeoutRef.current = setTimeout(() => {
        setMessage('');
        messageTimeoutRef.current = null;
      }, 10000);
    } catch (error) {
      setMessage('שגיאה בשמירת הנתונים: ' + error.message);
      
      // הסתר את ההודעה אחרי 15 שניות
      messageTimeoutRef.current = setTimeout(() => {
        setMessage('');
        messageTimeoutRef.current = null;
      }, 15000);
    } finally {
      setUploading(false);
    }
  };

  // הורדת קובץ לדוגמא
  const handleDownloadExample = () => {
    const link = document.createElement('a');
    link.href = '/Sample.xlsx';
    link.download = 'Sample.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // נקה timeout קודם אם יש
    if (messageTimeoutRef.current) {
      clearTimeout(messageTimeoutRef.current);
    }
    
    setMessage('קובץ דוגמא הורד בהצלחה!');
    
    // הסתר את ההודעה אחרי 10 שניות
    messageTimeoutRef.current = setTimeout(() => {
      setMessage('');
      messageTimeoutRef.current = null;
    }, 10000);
  };

  // הוספת בניין חדש
  const handleAddBuilding = () => {
    const newId = buildings.length > 0 ? Math.max(...buildings.map(b => b.id)) + 1 : 0;
    const newBuilding = {
      id: newId,
      name: '',
      number: '',
      numFlrs: 0,
      numAprts: 0
    };
    setBuildings([...buildings, newBuilding]);
    setSelectedBuildingId(newId);
    markDirty();
    setHasNoData(false);
    setIsFromServer(false);
  };

  // עדכון בניין
  const handleUpdateBuilding = (id, field, value) => {
    // וודא ש-numFlrs ו-numAprts הם מספרים שלמים
    let normalizedValue = value;
    if (field === 'numFlrs' || field === 'numAprts') {
      normalizedValue = Math.floor(Number(value)) || 0;
    }
    // וודא שמספר בניין הוא מספרי בלבד
    if (field === 'number') {
      // הסר כל תו שאינו ספרה
      normalizedValue = String(value).replace(/[^0-9]/g, '');
    }
    
    setBuildings(buildings.map(b => 
      b.id === id ? { ...b, [field]: normalizedValue } : b
    ));
    markDirty();
  };

  // מחיקת בניין
  const handleDeleteBuilding = (id) => {
    setBuildings(buildings.filter(b => b.id !== id));
    const newBoqItems = { ...boqItems };
    // מחק גם עם מפתח מספר וגם עם מפתח מחרוזת
    delete newBoqItems[id];
    delete newBoqItems[String(id)];
    setBoqItems(newBoqItems);
    if (selectedBuildingId === id && buildings.length > 1) {
      setSelectedBuildingId(buildings.find(b => b.id !== id)?.id || null);
    }
    markDirty();
  };

  // הוספת דייר חדש
  const handleAddTenant = () => {
    const newTenantId = tenants.length > 0 ? Math.max(...tenants.map(t => t.tenantId || 0)) + 1 : 0;
    const newTenant = {
      tenantId: newTenantId,
      name: '',
      family: '',
      id: '',
      phone1: '',
      phone2: '',
      email: '',
      note: '',
      building: '',
      floor: '',
      apartment: '',
      appType: '',
      AlwEmail: false,
      U_AlwSms: false,
      U_AlwWApp: false,
      isNew: isServerEditable,
    };
    setTenants([...tenants, newTenant]);
    markDirty();
  };

  // עדכון דייר
  const handleUpdateTenant = (tenantId, field, value) => {
    let normalizedValue = value;
    // וודא שתז הוא מספרי בלבד
    if (field === 'id') {
      // הסר כל תו שאינו ספרה
      normalizedValue = String(value).replace(/[^0-9]/g, '');
    }
    
    setTenants(tenants.map(t => 
      t.tenantId === tenantId ? { ...t, [field]: normalizedValue } : t
    ));
    markDirty();
  };

  // מחיקת דייר
  const handleDeleteTenant = (tenantId) => {
    setTenants(tenants.filter(t => t.tenantId !== tenantId));
    markDirty();
  };

  // הוספת פריט כתב כמויות
  const handleAddBoqItem = (buildingId) => {
    const key = String(buildingId);
    if (!boqItems[key]) {
      boqItems[key] = [];
    }
    const items = boqItems[key];
    const newId = items.length > 0 ? Math.max(...items.map(i => i.id)) + 1 : 0;
    const newItem = {
      id: newId,
      buildingId: buildingId,
      name: '',
      code: '',
      room: '',
      aprt: '',
      unit: '',
      quantity: 0,
      wstgPrcnt: 0,
      bufferQty: 0,
      shipDate: ''
    };
    setBoqItems({ ...boqItems, [key]: [...items, newItem] });
    markDirty();
  };

  // עדכון פריט כתב כמויות
  const handleUpdateBoqItem = (buildingId, itemId, fieldOrUpdates, value) => {
    const key = String(buildingId);
    const updates = typeof fieldOrUpdates === 'string'
      ? { [fieldOrUpdates]: value }
      : fieldOrUpdates;

    setBoqItems((prev) => ({
      ...prev,
      [key]: (prev[key] || []).map((item) =>
        item.id === itemId ? { ...item, ...updates } : item
      ),
    }));
    markDirty();
  };

  // מחיקת פריט כתב כמויות
  const handleDeleteBoqItem = (buildingId, itemId) => {
    const key = String(buildingId);
    const items = boqItems[key] || [];
    setBoqItems({
      ...boqItems,
      [key]: items.filter(item => item.id !== itemId)
    });
    markDirty();
  };

  if (loading) {
    return (
      <div style={{ padding: 8, textAlign: 'center' }}>
        <div>טוען...</div>
      </div>
    );
  }

  // אם יש שגיאה - הצג שגיאה (לא כפתורים)
  if (error) {
    return (
      <div className="boq-container">
        <div style={{ padding: 16, textAlign: 'center', color: 'red', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
          <h3 style={{ marginTop: 0 }}>שגיאה בטעינת הנתונים</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // אם אין נתונים (ולא שגיאה) - הצג אפשרויות
  if (hasNoData && !error && !loading) {
    return (
      <div className="boq-container">
        <div className="boq-empty-state">
          <h2>כתב כמויות</h2>
          
          {message && (
            <div className={`message ${message.includes('שגיאה') ? 'error-message' : 'success-message'}`}>
              {message}
            </div>
          )}

          <div className="boq-empty-actions">
            <button 
              className="boq-btn boq-btn-primary"
              onClick={handleDownloadExample}
            >
              <span style={{ fontSize: '18px', marginLeft: '8px' }}>📥</span>
              <span>הורד קובץ כ.כמויות לדוגמא</span>
            </button>

            <button
              type="button"
              className="boq-btn boq-btn-primary"
              onClick={() => handleUploadClick(fileUploadBoqRef)}
              disabled={uploading}
            >
              <span style={{ fontSize: '18px', marginLeft: '8px' }}>📤</span>
              <span>העלאת כתב כמויות</span>
            </button>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              ref={fileUploadBoqRef}
              style={{ display: 'none' }}
              disabled={uploading}
            />
            
            <button 
              className="boq-btn boq-btn-secondary"
              onClick={handleAddBuilding}
            >
              <span style={{ fontSize: '18px', marginLeft: '8px' }}>➕</span>
              <span>התחל הזנה ידנית</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // הצגת הנתונים
  const showAllBuildings = selectedBuildingId === 'all';
  const displayBuildingId = showAllBuildings ? null : (selectedBuildingId !== null ? selectedBuildingId : (buildings.length > 0 ? buildings[0].id : null));
  
  // אם בוחרים "הצג הכל", אוסף את כל הפריטים מכל הבניינים
  const currentBoqItems = showAllBuildings
    ? allBoqItemsFlat
    : (displayBuildingId !== null
        ? resolveBoqItemsForBuilding(boqItems, buildings, displayBuildingId)
        : []);
  
  const selectedBuilding = buildings.find(b => b.id === displayBuildingId);

  return (
    <div className="report-panel">
    <div className="boq-container">
      {message && (
        <div className={`message ${message.includes('שגיאה') ? 'error-message' : 'success-message'}`}>
          {message}
        </div>
      )}

      {((isFullEditable || (isServerEditable && isDirty))
        && (validation.errorCount > 0 || validation.warningCount > 0)) && (
        <BoqValidationPanel validation={validation} />
      )}

      {/* כפתורי פעולה */}
      <div className="boq-header-bar">
        <div className="boq-header-right">
          <div className="boq-header-buttons">
            {!isFromServer && (
              <>
                <button 
                  className="boq-btn-tiny boq-btn-primary"
                  onClick={handleDownloadExample}
                  title="הורד קובץ דוגמא"
                >
                  <span style={{ fontSize: '16px' }}>📥</span>
                  <span>הורד דוגמא</span>
                </button>
                <button
                  type="button"
                  className="boq-btn-tiny boq-btn-primary"
                  onClick={() => handleUploadClick(fileUploadBoqExistingRef)}
                  title="העלה קובץ"
                  disabled={uploading}
                >
                  <span style={{ fontSize: '16px' }}>📤</span>
                  <span>העלה קובץ</span>
                </button>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  ref={fileUploadBoqExistingRef}
                  style={{ display: 'none' }}
                  disabled={uploading}
                />
              </>
            )}
            {showSaveButton && (
              <button
                type="button"
                className={`boq-btn-tiny boq-btn-save boq-btn-save-icon${isDirty ? ' boq-btn-save--dirty' : ''}`}
                onClick={handleSave}
                disabled={uploading}
                title="שמור לשרת"
                aria-label={uploading ? 'שומר...' : 'שמור לשרת'}
                data-tooltip={uploading ? 'שומר...' : 'שמור לשרת'}
              >
                <span className="boq-btn-save-icon-glyph" aria-hidden="true">
                  {uploading ? '⏳' : '💾'}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* לשוניות */}
      <div className="boq-tabs">
        <div className="boq-tabs-header">
          <button
            className={`boq-tab ${activeTab === 'buildings' ? 'active' : ''}`}
            onClick={() => setActiveTab('buildings')}
          >
            🏢 רשימת בניינים
          </button>
          <button
            className={`boq-tab ${activeTab === 'tenants' ? 'active' : ''}`}
            onClick={() => setActiveTab('tenants')}
          >
            📋 רשימת דיירים
          </button>
          <button
            className={`boq-tab ${activeTab === 'boq' ? 'active' : ''}`}
            onClick={() => setActiveTab('boq')}
          >
            📐 כתב כמויות
          </button>
        </div>

        <div className="boq-tab-content">
          {/* לשונית רשימת בניינים */}
          {activeTab === 'buildings' && (
            <div className="boq-section">
              {isFullEditable && (
                <div className="boq-section-header">
                  <button 
                    className="boq-btn boq-btn-small"
                    onClick={handleAddBuilding}
                  >
                    ➕ הוסף בניין
                  </button>
                </div>
              )}
              <BuildingsListTable 
                buildings={buildings}
                selectedId={selectedBuildingId}
                onSelect={setSelectedBuildingId}
                onUpdate={handleUpdateBuilding}
                onDelete={handleDeleteBuilding}
                canEdit={isFullEditable}
                rowErrors={validation.buildingRows}
              />
            </div>
          )}

          {/* לשונית רשימת דיירים */}
          {activeTab === 'tenants' && (
            <div className="boq-section">
              {tenantsEditMode !== 'none' && (
                <div className="boq-section-header">
                  <button 
                    className="boq-btn boq-btn-small"
                    onClick={handleAddTenant}
                  >
                    ➕ הוסף דייר
                  </button>
                </div>
              )}
              <TenantsTable 
                tenants={tenants}
                buildings={buildings}
                onUpdate={handleUpdateTenant}
                onDelete={handleDeleteTenant}
                editMode={tenantsEditMode}
                rowErrors={validation.tenantRows}
              />
            </div>
          )}

          {/* לשונית כתב כמויות */}
          {activeTab === 'boq' && (
            <div className="boq-section">
              <div className="boq-section-header">
                <div className="boq-building-select">
                  <label>בחר בניין:</label>
                  <select 
                    value={
                      selectedBuildingId === 'all'
                        ? 'all'
                        : selectedBuildingId !== null && selectedBuildingId !== undefined
                          ? String(selectedBuildingId)
                          : (buildings.length > 0 ? String(buildings[0].id) : '')
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === 'all') {
                        setSelectedBuildingId('all');
                      } else if (value === '') {
                        setSelectedBuildingId(null);
                      } else {
                        const parsed = Number(value);
                        setSelectedBuildingId(Number.isNaN(parsed) ? value : parsed);
                      }
                    }}
                  >
                    <option value="">-- בחר בניין --</option>
                    <option value="all">הצג הכל</option>
                    {buildings.map(b => {
                      const buildingNumber = b.number || `בניין ${b.id + 1}`;
                      const buildingName = b.name ? ` (${b.name})` : '';
                      return (
                        <option key={b.id} value={String(b.id)}>
                          {buildingNumber}{buildingName}
                        </option>
                      );
                    })}
                  </select>
                </div>
                {!showAllBuildings && displayBuildingId !== null && boqEditMode === 'full' && (
                  <button
                    type="button"
                    className="boq-btn boq-btn-small"
                    onClick={() => handleAddBoqItem(displayBuildingId)}
                  >
                    ➕ הוסף פריט
                  </button>
                )}
              </div>

              {(showAllBuildings || displayBuildingId !== null) ? (
                <>
                  
                  <BoqItemsTable 
                    key={showAllBuildings ? 'all' : String(displayBuildingId ?? 'none')}
                    items={currentBoqItems}
                    buildingId={showAllBuildings ? null : displayBuildingId}
                    buildings={buildings}
                    migvan={migvan}
                    allBoqItems={allBoqItemsFlat}
                    showBuildingColumn={showAllBuildings}
                    onUpdate={handleUpdateBoqItem}
                    onDelete={handleDeleteBoqItem}
                    editMode={boqEditMode}
                    rowErrors={validation.boqRows}
                  />
                </>
              ) : (
                <div className="boq-empty-table">אין בניינים. נא להוסיף בניין בלשונית "רשימת בניינים"</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    </div>
  );
}

function BoqValidationPanel({ validation }) {
  const [expanded, setExpanded] = useState(false);

  if (!validation || (validation.errorCount === 0 && validation.warningCount === 0)) {
    return null;
  }

  const hasDetails = validation.errors.length > 0 || validation.warnings.length > 0;

  return (
    <div className={`boq-validation-panel${validation.errorCount > 0 ? ' boq-validation-panel--error' : ' boq-validation-panel--warn'}${expanded ? ' boq-validation-panel--open' : ''}`}>
      <button
        type="button"
        className="boq-validation-header"
        onClick={() => hasDetails && setExpanded((v) => !v)}
        aria-expanded={expanded}
        disabled={!hasDetails}
      >
        <span className="boq-validation-chevron" aria-hidden="true">
          {expanded ? '▼' : '◀'}
        </span>
        <span className="boq-validation-summary">
          {validation.errorCount > 0 && (
            <strong>{validation.errorCount} שגיאות</strong>
          )}
          {validation.errorCount > 0 && validation.warningCount > 0 && ' · '}
          {validation.warningCount > 0 && (
            <span>{validation.warningCount} התראות</span>
          )}
          {' – שורות לא תקינות מסומנות באדום'}
        </span>
      </button>

      {expanded && hasDetails && (
        <ul className="boq-validation-list">
          {validation.errors.map((msg, index) => (
            <li key={`e-${index}-${msg}`} className="boq-validation-item boq-validation-item--error">{msg}</li>
          ))}
          {validation.warnings.map((msg, index) => (
            <li key={`w-${index}-${msg}`} className="boq-validation-item boq-validation-item--warn">{msg}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BoqPageIcon({ variant }) {
  const iconPaths = {
    first: (
      <>
        <path d="M12 5l-4 4 4 4" />
        <path d="M8 5l-4 4 4 4" />
      </>
    ),
    prev: <path d="M11 5L7 9l4 4" />,
    next: <path d="M7 5l4 4-4 4" />,
    last: (
      <>
        <path d="M6 5l4 4-4 4" />
        <path d="M10 5l4 4-4 4" />
      </>
    ),
  };

  return (
    <svg className="boq-page-icon" viewBox="0 0 16 16" aria-hidden="true">
      {iconPaths[variant]}
    </svg>
  );
}

function BoqTablePagination({ page, pageCount, pageSize, onPageChange, onPageSizeChange }) {
  if (pageCount <= 1) return null;

  return (
    <div className="boq-table-pagination">
      <button
        type="button"
        className="boq-page-btn"
        onClick={() => onPageChange(1)}
        disabled={page === 1}
        aria-label="עמוד ראשון"
        title="עמוד ראשון"
      >
        <BoqPageIcon variant="first" />
      </button>
      <button
        type="button"
        className="boq-page-btn"
        onClick={() => onPageChange(Math.max(1, page - 1))}
        disabled={page === 1}
        aria-label="עמוד קודם"
        title="עמוד קודם"
      >
        <BoqPageIcon variant="prev" />
      </button>
      <span className="boq-page-info">עמוד {page} מתוך {pageCount}</span>
      <button
        type="button"
        className="boq-page-btn"
        onClick={() => onPageChange(Math.min(pageCount, page + 1))}
        disabled={page === pageCount}
        aria-label="עמוד הבא"
        title="עמוד הבא"
      >
        <BoqPageIcon variant="next" />
      </button>
      <button
        type="button"
        className="boq-page-btn"
        onClick={() => onPageChange(pageCount)}
        disabled={page === pageCount}
        aria-label="עמוד אחרון"
        title="עמוד אחרון"
      >
        <BoqPageIcon variant="last" />
      </button>
      <div className="boq-page-size">
        <span>הצג</span>
        <select
          className="boq-page-size-select"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          aria-label="מספר שורות בעמוד"
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
        <span>בעמוד</span>
      </div>
    </div>
  );
}

// טבלת דיירים
function isTenantFieldEditable(editMode, tenant, field) {
  if (editMode === 'none') return false;
  if (editMode === 'full') return true;
  if (tenant.isNew) return true;
  return PARTIAL_TENANT_FIELDS.has(field);
}

function TenantsTable({ tenants, buildings, onUpdate, onDelete, editMode = 'none', rowErrors = {} }) {
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ field: null, direction: null });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const columns = [
    { field: 'name', label: 'שם פרטי' },
    { field: 'family', label: 'משפחה' },
    { field: 'id', label: 'תז' },
    { field: 'phone1', label: 'טלפון 1' },
    { field: 'phone2', label: 'טלפון 2' },
    { field: 'email', label: 'מייל' },
    { field: 'note', label: 'הערות' },
    { field: 'building', label: 'בניין' },
    { field: 'floor', label: 'קומה' },
    { field: 'apartment', label: 'דירה' },
    { field: 'appType', label: 'טיפוס' },
    { field: 'AlwEmail', label: 'מייל', title: 'מורשה שליחה במייל', type: 'checkbox' },
    { field: 'U_AlwSms', label: 'הודעה', title: 'מורשה שליחת הודעה', type: 'checkbox' },
    { field: 'U_AlwWApp', label: 'וואצאפ', title: 'מורשה שליחה בוואצאפ', type: 'checkbox' }
  ];

  // סינון ומיון
  const filteredData = useMemo(() => {
    let filtered = tenants;
    
    // סינון עמודות
    Object.entries(filters).forEach(([field, filterObj]) => {
      if (!filterObj) return;
      if (filterObj.select && Array.isArray(filterObj.select) && filterObj.select.length > 0) {
        filtered = filtered.filter(row => filterObj.select.includes(String(row[field] || '')));
      }
      if (filterObj.input) {
        filtered = filtered.filter(row => String(row[field] || '').toLowerCase().includes(filterObj.input.toLowerCase()));
      }
    });
    
    // מיון
    if (sort.field) {
      filtered = [...filtered].sort((a, b) => {
        const av = a[sort.field];
        const bv = b[sort.field];
        if (av === bv) return 0;
        if (sort.direction === 'asc') return av > bv ? 1 : -1;
        return av < bv ? 1 : -1;
      });
    }
    
    return filtered;
  }, [tenants, filters, sort, columns]);

  // דפדוף
  const pageCount = Math.ceil(filteredData.length / pageSize);
  const pagedData = filteredData.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (field) => {
    if (sort.field === field) {
      if (sort.direction === 'asc') {
        setSort({ field, direction: 'desc' });
      } else {
        setSort({ field: null, direction: null });
      }
    } else {
      setSort({ field, direction: 'asc' });
    }
  };

  const renderCell = (tenant, field, type = 'text') => {
    const fieldEditable = isTenantFieldEditable(editMode, tenant, field);
    const isCheckboxField = field === 'AlwEmail' || field === 'U_AlwSms' || field === 'U_AlwWApp';
    const isIdField = field === 'id';
    const isBuildingField = field === 'building';
    const isNumericField = field === 'floor' || field === 'apartment';
    
    if (fieldEditable) {
      if (type === 'checkbox' || isCheckboxField) {
        return (
          <input
            type="checkbox"
            checked={tenant[field] || false}
            onChange={(e) => onUpdate(tenant.tenantId, field, e.target.checked)}
            className="boq-checkbox"
          />
        );
      }
      if (isBuildingField) {
        // שדה בניין - dropdown מרשימת הבניינים
        return (
          <select
            value={tenant[field] || ''}
            onChange={(e) => {
              const selectedBuildingNumber = e.target.value;
              // שמור את מספר הבניין
              onUpdate(tenant.tenantId, field, selectedBuildingNumber);
            }}
            className="boq-input"
            style={{ width: '100%', padding: '2px 4px', fontSize: '10px' }}
          >
            <option value="">-- בחר בניין --</option>
            {buildings.map(b => {
              const buildingNumber = String(b.number || '').trim();
              if (!buildingNumber) return null;
              return (
                <option key={b.id} value={buildingNumber}>
                  {buildingNumber}
                </option>
              );
            })}
          </select>
        );
      }
      if (isNumericField) {
        // שדות קומה ודירה - רק מספרים
        return (
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={tenant[field] || ''}
            onChange={(e) => {
              const value = e.target.value;
              // רק מספרים - הסר כל תו שאינו ספרה
              const numericValue = value.replace(/[^0-9]/g, '');
              onUpdate(tenant.tenantId, field, numericValue);
            }}
            onKeyPress={(e) => {
              // אפשר רק ספרות
              if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Tab') {
                e.preventDefault();
              }
            }}
            onPaste={(e) => {
              e.preventDefault();
              const pastedText = e.clipboardData.getData('text');
              // הסר כל תו שאינו ספרה מהטקסט שהודבק
              const numericValue = pastedText.replace(/[^0-9]/g, '');
              onUpdate(tenant.tenantId, field, numericValue);
            }}
            className="boq-input"
          />
        );
      }
      if (isIdField) {
        // שדה תז - רק מספרים
        return (
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={tenant[field] || ''}
            onChange={(e) => {
              const value = e.target.value;
              // רק מספרים - הסר כל תו שאינו ספרה
              const numericValue = value.replace(/[^0-9]/g, '');
              onUpdate(tenant.tenantId, field, numericValue);
            }}
            onKeyPress={(e) => {
              // אפשר רק ספרות
              if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Tab') {
                e.preventDefault();
              }
            }}
            onPaste={(e) => {
              e.preventDefault();
              const pastedText = e.clipboardData.getData('text');
              // הסר כל תו שאינו ספרה מהטקסט שהודבק
              const numericValue = pastedText.replace(/[^0-9]/g, '');
              onUpdate(tenant.tenantId, field, numericValue);
            }}
            className="boq-input"
          />
        );
      }
      return (
        <input
          type={type}
          value={tenant[field] || ''}
          onChange={(e) => onUpdate(tenant.tenantId, field, e.target.value)}
          className="boq-input"
        />
      );
    }
    if (type === 'checkbox' || isCheckboxField) {
      return tenant[field] ? '✓' : '';
    }
    return tenant[field] || '';
  };

  if (tenants.length === 0) {
    return <div className="boq-empty-table">אין דיירים</div>;
  }

  return (
    <div className="boq-items-panel">
      <div className="boq-table-wrapper boq-table-wrapper--fill">
        <table className="boq-table">
          <thead>
            <tr>
              {columns.map(col => (
                <th 
                  key={col.field}
                  title={col.title || col.label}
                  onDoubleClick={() => handleSort(col.field)}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'space-between' }}>
                    <span>{col.label}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {sort.field === col.field && (
                        <span>{sort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                      {col.type !== 'checkbox' && (
                        <FilterCell
                          column={col}
                          data={tenants}
                          value={filters[col.field] || {}}
                          onChange={(type, value) => {
                            setFilters(prev => ({
                              ...prev,
                              [col.field]: { ...prev[col.field], [type]: value }
                            }));
                            setPage(1);
                          }}
                        />
                      )}
                    </div>
                  </div>
                </th>
              ))}
              {editMode !== 'none' && <th>פעולות</th>}
            </tr>
          </thead>
          <tbody>
            {pagedData.map(tenant => {
              const rowIssues = rowErrors[tenant.tenantId];
              const canDeleteRow = editMode === 'full' || (editMode === 'partial' && tenant.isNew);
              return (
              <tr
                key={tenant.tenantId}
                className={rowIssues?.length ? 'boq-row-invalid' : ''}
                title={rowIssues?.join(' • ') || undefined}
              >
                {columns.map(col => (
                  <td key={col.field} style={col.type === 'checkbox' ? { textAlign: 'center' } : {}}>
                    {renderCell(tenant, col.field, col.type)}
                  </td>
                ))}
                {canDeleteRow && (
                  <td>
                    <button
                      className="boq-btn-icon"
                      onClick={() => onDelete(tenant.tenantId)}
                      title="מחק"
                    >
                      🗑️
                    </button>
                  </td>
                )}
                {!canDeleteRow && editMode !== 'none' && <td />}
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>
      
      <BoqTablePagination
        page={page}
        pageCount={pageCount}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />
    </div>
  );
}

// טבלת רשימת בניינים
function BuildingsListTable({ buildings, selectedId, onSelect, onUpdate, onDelete, canEdit, rowErrors = {} }) {
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ field: null, direction: null });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const columns = [
    { field: 'name', label: 'תאור' },
    { field: 'number', label: 'מספר בניין' },
    { field: 'numFlrs', label: 'מספר קומות' },
    { field: 'numAprts', label: 'מספר דירות' }
  ];

  // סינון ומיון
  const filteredData = useMemo(() => {
    let filtered = buildings;
    
    // סינון עמודות
    Object.entries(filters).forEach(([field, filterObj]) => {
      if (!filterObj) return;
      if (filterObj.select && Array.isArray(filterObj.select) && filterObj.select.length > 0) {
        filtered = filtered.filter(row => filterObj.select.includes(String(row[field] || '')));
      }
      if (filterObj.input) {
        filtered = filtered.filter(row => String(row[field] || '').toLowerCase().includes(filterObj.input.toLowerCase()));
      }
    });
    
    // מיון
    if (sort.field) {
      filtered = [...filtered].sort((a, b) => {
        const av = a[sort.field];
        const bv = b[sort.field];
        if (av === bv) return 0;
        if (sort.direction === 'asc') return av > bv ? 1 : -1;
        return av < bv ? 1 : -1;
      });
    }
    
    return filtered;
  }, [buildings, filters, sort, columns]);

  // דפדוף
  const pageCount = Math.ceil(filteredData.length / pageSize);
  const pagedData = filteredData.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (field) => {
    if (sort.field === field) {
      if (sort.direction === 'asc') {
        setSort({ field, direction: 'desc' });
      } else {
        setSort({ field: null, direction: null });
      }
    } else {
      setSort({ field, direction: 'asc' });
    }
  };

  if (buildings.length === 0) {
    return <div className="boq-empty-table">אין בניינים</div>;
  }

  return (
    <div className="boq-items-panel">
      <div className="boq-table-wrapper boq-table-wrapper--fill">
        <table className="boq-table">
          <thead>
            <tr>
              {columns.map(col => (
                <th 
                  key={col.field}
                  onDoubleClick={() => handleSort(col.field)}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'space-between' }}>
                    <span>{col.label}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {sort.field === col.field && (
                        <span>{sort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                      <FilterCell
                        column={col}
                        data={buildings}
                        value={filters[col.field] || {}}
                        onChange={(type, value) => {
                          setFilters(prev => ({
                            ...prev,
                            [col.field]: { ...prev[col.field], [type]: value }
                          }));
                          setPage(1);
                        }}
                      />
                    </div>
                  </div>
                </th>
              ))}
              {canEdit && <th>פעולות</th>}
            </tr>
          </thead>
          <tbody>
            {pagedData.map(building => {
              const rowIssues = rowErrors[building.id];
              return (
              <tr 
                key={building.id}
                className={[
                  selectedId === building.id ? 'boq-row-selected' : '',
                  rowIssues?.length ? 'boq-row-invalid' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => onSelect(building.id)}
                style={{ cursor: 'pointer' }}
                title={rowIssues?.join(' • ') || undefined}
              >
                <td>
                  {canEdit ? (
                    <input
                      type="text"
                      value={building.name || ''}
                      onChange={(e) => onUpdate(building.id, 'name', e.target.value)}
                      className="boq-input"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    building.name
                  )}
                </td>
                <td>
                  {canEdit ? (
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={building.number || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        // רק מספרים שלמים - הסר כל תו שאינו ספרה
                        const numericValue = value.replace(/[^0-9]/g, '');
                        onUpdate(building.id, 'number', numericValue);
                      }}
                      onKeyPress={(e) => {
                        // אפשר רק ספרות
                        if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Tab') {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pastedText = e.clipboardData.getData('text');
                        // הסר כל תו שאינו ספרה מהטקסט שהודבק
                        const numericValue = pastedText.replace(/[^0-9]/g, '');
                        onUpdate(building.id, 'number', numericValue);
                      }}
                      className="boq-input"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    building.number
                  )}
                </td>
                <td>
                  {canEdit ? (
                    <input
                      type="number"
                      step="1"
                      min="0"
                      value={building.numFlrs || 0}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        onUpdate(building.id, 'numFlrs', Math.max(0, value));
                      }}
                      className="boq-input"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    Math.floor(building.numFlrs || 0)
                  )}
                </td>
                <td>
                  {canEdit ? (
                    <input
                      type="number"
                      step="1"
                      min="0"
                      value={building.numAprts || 0}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        onUpdate(building.id, 'numAprts', Math.max(0, value));
                      }}
                      className="boq-input"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    Math.floor(building.numAprts || 0)
                  )}
                </td>
                {canEdit && (
                  <td>
                    <button
                      className="boq-btn-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(building.id);
                      }}
                      title="מחק"
                    >
                      🗑️
                    </button>
                  </td>
                )}
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>
      
      <BoqTablePagination
        page={page}
        pageCount={pageCount}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />
    </div>
  );
}

// טבלת פריטי כתב כמויות
// פונקציה להמרת תאריך לפורמט dd/MM/yyyy
const formatDate = (dateValue) => {
  if (!dateValue) return '';
  
  try {
    let date;
    
    // אם זה כבר Date object
    if (dateValue instanceof Date) {
      date = dateValue;
    }
    // אם זה string - נסה לפרסר
    else if (typeof dateValue === 'string') {
      const dmyMatch = dateValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (dmyMatch) {
        const [, day, month, year] = dmyMatch;
        date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
      }
      // אם זה בפורמט SQL (YYYY-MM-DD) - פרסר ידנית
      else {
        const sqlDateMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (sqlDateMatch) {
          const [, year, month, day] = sqlDateMatch;
          date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
        } else {
          // נסה לפרסר תאריך ISO או פורמטים אחרים
          date = new Date(dateValue);
        }
      }
      
      // בדוק אם התאריך תקין
      if (isNaN(date.getTime())) {
        return dateValue; // אם לא הצלחנו לפרסר - החזר את הערך המקורי
      }
    }
    // אם זה מספר (timestamp או תאריך Excel)
    else if (typeof dateValue === 'number') {
      if (dateValue > 25569 && dateValue < 100000) {
        date = new Date(Math.round((dateValue - 25569) * 86400 * 1000));
      } else {
        date = new Date(dateValue);
      }
    }
    else {
      return String(dateValue);
    }
    
    // בדוק אם השנה קטנה מ-2025 - אם כן, החזר ריק
    const year = date.getFullYear();
    if (year < 2025) {
      return '';
    }
    
    // המר ל-dd/MM/yyyy
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    
    return `${day}/${month}/${year}`;
  } catch (error) {
    // אם יש שגיאה - החזר את הערך המקורי
    return String(dateValue);
  }
};

const toDateInputValue = (dateValue) => {
  if (dateValue === null || dateValue === undefined || dateValue === '') return '';

  if (typeof dateValue === 'string') {
    if (dateValue.includes('/')) {
      const [day, month, year] = dateValue.split('/');
      if (day && month && year) {
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }
    const sqlMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (sqlMatch) return dateValue.slice(0, 10);
  }

  const formatted = formatDate(dateValue);
  if (!formatted || !formatted.includes('/')) return '';
  const [day, month, year] = formatted.split('/');
  return `${year}-${month}-${day}`;
};

const normalizeShipDate = (dateValue) => {
  if (dateValue === null || dateValue === undefined || dateValue === '') return '';
  if (typeof dateValue === 'string') return dateValue;
  return formatDate(dateValue);
};

const TOOLTIP_VIEWPORT_PADDING = 12;
const TOOLTIP_GAP = 8;
const TOOLTIP_ESTIMATED_HEIGHT = 176;
const TOOLTIP_ESTIMATED_WIDTH = 280;

const computeMigvanTooltipPosition = (anchorRect, tooltipSize = {}) => {
  if (!anchorRect) return null;

  const tooltipHeight = tooltipSize.height || TOOLTIP_ESTIMATED_HEIGHT;
  const tooltipWidth = tooltipSize.width || TOOLTIP_ESTIMATED_WIDTH;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let left = anchorRect.left;
  if (left + tooltipWidth > viewportWidth - TOOLTIP_VIEWPORT_PADDING) {
    left = viewportWidth - tooltipWidth - TOOLTIP_VIEWPORT_PADDING;
  }
  left = Math.max(TOOLTIP_VIEWPORT_PADDING, left);

  const spaceBelow = viewportHeight - anchorRect.bottom - TOOLTIP_GAP - TOOLTIP_VIEWPORT_PADDING;
  const spaceAbove = anchorRect.top - TOOLTIP_GAP - TOOLTIP_VIEWPORT_PADDING;

  let top = anchorRect.bottom + TOOLTIP_GAP;
  if (spaceBelow < tooltipHeight && spaceAbove > spaceBelow) {
    top = anchorRect.top - tooltipHeight - TOOLTIP_GAP;
  }

  top = Math.max(
    TOOLTIP_VIEWPORT_PADDING,
    Math.min(top, viewportHeight - tooltipHeight - TOOLTIP_VIEWPORT_PADDING)
  );

  return { top, left };
};

function MigvanCodeTooltip({ info, position, tooltipRef }) {
  if (!info || !position) return null;

  return createPortal(
    <div
      ref={tooltipRef}
      className="boq-migvan-tooltip"
      style={{ top: position.top, left: position.left }}
      role="tooltip"
    >
      <div className="boq-migvan-tooltip-row">
        <span className="boq-migvan-tooltip-label">קוד פריט מגוון:</span>
        <span>{info.code}</span>
      </div>
      <div className="boq-migvan-tooltip-row">
        <span className="boq-migvan-tooltip-label">תיאור:</span>
        <span>{info.description || '—'}</span>
      </div>
      <div className="boq-migvan-tooltip-row">
        <span className="boq-migvan-tooltip-label">י.מידה:</span>
        <span>{info.unit || '—'}</span>
      </div>
      <div className="boq-migvan-tooltip-row">
        <span className="boq-migvan-tooltip-label">סך כמות – בניין נוכחי:</span>
        <span>{info.currentBuildingQtyLabel}{info.unit ? ` ${info.unit}` : ''}</span>
      </div>
      <div className="boq-migvan-tooltip-row">
        <span className="boq-migvan-tooltip-label">סך כמות – כל הבניינים:</span>
        <span>{info.allBuildingsQtyLabel}{info.unit ? ` ${info.unit}` : ''}</span>
      </div>
    </div>,
    document.body
  );
}

function MigvanCodeCell({
  item,
  itemBuildingId,
  migvan,
  allBoqItems,
  invalidCode,
}) {
  const cellRef = useRef(null);
  const tooltipRef = useRef(null);
  const [tooltipState, setTooltipState] = useState(null);

  const tooltipInfo = useMemo(() => buildMigvanTooltipInfo({
    code: item.code,
    item,
    migvan,
    allBoqItems,
    itemBuildingId,
  }), [item, migvan, allBoqItems, itemBuildingId]);

  const getTooltipPosition = useCallback((measuredSize) => {
    const rect = cellRef.current?.getBoundingClientRect();
    return computeMigvanTooltipPosition(rect, measuredSize);
  }, []);

  const syncTooltipPosition = useCallback(() => {
    setTooltipState((prev) => {
      if (!prev) return prev;
      const measuredSize = tooltipRef.current
        ? {
            height: tooltipRef.current.offsetHeight,
            width: tooltipRef.current.offsetWidth,
          }
        : undefined;
      const position = getTooltipPosition(measuredSize);
      if (!position) return prev;
      if (prev.position.top === position.top && prev.position.left === position.left) {
        return prev;
      }
      return { ...prev, position };
    });
  }, [getTooltipPosition]);

  const handleMouseEnter = () => {
    if (!tooltipInfo) return;
    const position = getTooltipPosition();
    if (!position) return;
    setTooltipState({ info: tooltipInfo, position });
  };

  const handleMouseLeave = () => {
    setTooltipState(null);
  };

  useLayoutEffect(() => {
    if (!tooltipState) return undefined;
    syncTooltipPosition();
    return undefined;
  }, [tooltipState?.info, syncTooltipPosition]);

  useEffect(() => {
    if (!tooltipState) return undefined;

    const handleReposition = () => syncTooltipPosition();
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [tooltipState, syncTooltipPosition]);

  return (
    <>
      <td
        ref={cellRef}
        className={tooltipInfo ? 'boq-migvan-code-cell boq-migvan-code-cell--has-tooltip' : 'boq-migvan-code-cell'}
        style={{ width: '50px', maxWidth: '50px', minWidth: '50px', padding: '2px', position: 'relative' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <input
          type="text"
          value={item.code || ''}
          readOnly
          tabIndex={-1}
          className="boq-input"
          style={{
            width: '100%',
            padding: '1px 2px',
            fontSize: '9px',
            backgroundColor: '#f5f5f5',
            cursor: tooltipInfo ? 'help' : 'not-allowed',
            maxWidth: '50px',
            minWidth: '50px',
            textAlign: 'center',
            border: invalidCode ? '1px solid red' : '1px solid #ddd',
          }}
        />
        {invalidCode && (
          <span
            title={`קוד מגוון בקובץ: ${item.code || ''}\nקוד לא תקין`}
            style={{
              position: 'absolute',
              top: '2px',
              right: '2px',
              color: 'red',
              fontSize: '12px',
              cursor: 'help',
              zIndex: 10,
            }}
          >
            ⚠️
          </span>
        )}
      </td>
      {tooltipState && (
        <MigvanCodeTooltip
          info={tooltipState.info}
          position={tooltipState.position}
          tooltipRef={tooltipRef}
        />
      )}
    </>
  );
}

const BOQ_QUANTITY_FIELDS = new Set(['quantity', 'wstgPrcnt', 'bufferQty', 'shipDate']);

function isBoqFieldEditable(editMode, field) {
  if (editMode === 'none') return false;
  if (editMode === 'full') {
    return !['code', 'unit'].includes(field);
  }
  if (editMode === 'quantities') {
    return BOQ_QUANTITY_FIELDS.has(field);
  }
  return false;
}

function BoqItemsTable({ items, buildingId, buildings = [], migvan = [], allBoqItems = [], showBuildingColumn = false, onUpdate, onDelete, editMode = 'none', rowErrors = {} }) {
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ field: null, direction: null });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const minShipDate = useMemo(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const columns = [
    ...(showBuildingColumn ? [{ field: 'buildingId', label: 'בניין' }] : []),
    { field: 'name', label: 'מגוון' },
    { field: 'code', label: 'קוד מגוון', type: 'readonly' },
    { field: 'room', label: 'חדר' },
    { field: 'aprt', label: 'דירה' },
    { field: 'unit', label: 'י.מידה', type: 'readonly' },
    { field: 'quantity', label: 'כמות לפני העמסה' },
    { field: 'wstgPrcnt', label: 'אחוז פחת' },
    { field: 'bufferQty', label: 'כמות לבדק' },
    { field: 'shipDate', label: 'ת.אספקה משוער', type: 'date' }
  ];

  // סינון ומיון
  const filteredData = useMemo(() => {
    let filtered = items;
    
    // סינון עמודות
    Object.entries(filters).forEach(([field, filterObj]) => {
      if (!filterObj) return;
      if (filterObj.select && Array.isArray(filterObj.select) && filterObj.select.length > 0) {
        filtered = filtered.filter(row => filterObj.select.includes(String(row[field] || '')));
      }
      if (filterObj.input) {
        filtered = filtered.filter(row => String(row[field] || '').toLowerCase().includes(filterObj.input.toLowerCase()));
      }
    });
    
    // מיון
    if (sort.field) {
      filtered = [...filtered].sort((a, b) => {
        let av = a[sort.field];
        let bv = b[sort.field];
        
        // אם מיון לפי בניין, השתמש בשם הבניין
        if (sort.field === 'buildingId' && showBuildingColumn) {
          const buildingA = buildings.find(b => b.id === av);
          const buildingB = buildings.find(b => b.id === bv);
          av = buildingA ? `${buildingA.number || av} ${buildingA.name || ''}` : av;
          bv = buildingB ? `${buildingB.number || bv} ${buildingB.name || ''}` : bv;
        }
        
        if (av === bv) return 0;
        if (sort.direction === 'asc') return av > bv ? 1 : -1;
        return av < bv ? 1 : -1;
      });
    }
    
    return filtered;
  }, [items, filters, sort, columns, showBuildingColumn, buildings]);

  // דפדוף
  const pageCount = Math.ceil(filteredData.length / pageSize);
  const pagedData = filteredData.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (field) => {
    if (sort.field === field) {
      if (sort.direction === 'asc') {
        setSort({ field, direction: 'desc' });
      } else {
        setSort({ field: null, direction: null });
      }
    } else {
      setSort({ field, direction: 'asc' });
    }
  };

  if (items.length === 0) {
    return <div className="boq-empty-table">אין פריטים לבניין זה</div>;
  }

  return (
    <div className="boq-items-panel">
      <div className="boq-table-wrapper boq-table-wrapper--fill">
        <table className="boq-table">
          <thead>
            <tr>
              {columns.map(col => (
                <th 
                  key={col.field}
                  onDoubleClick={() => handleSort(col.field)}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'space-between' }}>
                    <span>{col.label}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {sort.field === col.field && (
                        <span>{sort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                      {col.type !== 'date' && col.type !== 'readonly' && (
                        <FilterCell
                          column={col}
                          data={items}
                          value={filters[col.field] || {}}
                          onChange={(type, value) => {
                            setFilters(prev => ({
                              ...prev,
                              [col.field]: { ...prev[col.field], [type]: value }
                            }));
                            setPage(1);
                          }}
                        />
                      )}
                    </div>
                  </div>
                </th>
              ))}
              {editMode === 'full' && <th>פעולות</th>}
            </tr>
          </thead>
          <tbody>
            {pagedData.map(item => {
              const itemBuildingId = showBuildingColumn ? item.buildingId : buildingId;
              const rowKey = `${itemBuildingId}:${item.id}`;
              const rowIssues = rowErrors[rowKey];
              const quantityIsWhole = isPieceUnit(item.unit);
              return (
              <tr
                key={`${itemBuildingId}-${item.id}`}
                className={rowIssues?.length ? 'boq-row-invalid' : ''}
                title={rowIssues?.join(' • ') || undefined}
              >
                {showBuildingColumn && (
                  <td>
                    {(() => {
                      // מצא את הבניין לפי buildingId של הפריט
                      const building = buildings.find(b => b.id === item.buildingId);
                      if (building) {
                        const buildingNumber = building.number || building.id || '';
                        const buildingName = building.name ? ` (${building.name})` : '';
                        return buildingNumber ? `${buildingNumber}${buildingName}` : `בניין ${building.id}`;
                      }
                      // אם לא נמצא, נסה להשתמש ב-itemBuildingId
                      const fallbackBuilding = buildings.find(b => b.id === itemBuildingId);
                      if (fallbackBuilding) {
                        const buildingNumber = fallbackBuilding.number || fallbackBuilding.id || '';
                        const buildingName = fallbackBuilding.name ? ` (${fallbackBuilding.name})` : '';
                        return buildingNumber ? `${buildingNumber}${buildingName}` : `בניין ${fallbackBuilding.id}`;
                      }
                      return `בניין ${item.buildingId || itemBuildingId}`;
                    })()}
                  </td>
                )}
                <td>
                  {isBoqFieldEditable(editMode, 'name') ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', position: 'relative' }}>
                      <select
                        value={item.name || ''}
                        onChange={(e) => {
                          const selectedName = e.target.value;
                          // מצא את המגוון הנבחר ברשימת migvan
                          const selectedMigvan = migvan.find(
                            (m) => (m.name || '').trim() === selectedName.trim()
                          );
                          if (selectedMigvan) {
                            onUpdate(itemBuildingId, item.id, {
                              name: selectedMigvan.name,
                              code: selectedMigvan.code || '',
                              unit: selectedMigvan.unit || '',
                              invalidCode: false,
                            });
                          } else {
                            onUpdate(itemBuildingId, item.id, 'name', selectedName);
                          }
                        }}
                        className="boq-input"
                        style={{ 
                          width: '100%', 
                          padding: '2px 4px', 
                          fontSize: '10px',
                          flex: 1
                        }}
                      >
                        <option value="">-- בחר מגוון --</option>
                        {migvan.map((m, idx) => (
                          <option key={idx} value={m.name}>
                            {m.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    item.name
                  )}
                </td>
                <MigvanCodeCell
                  item={item}
                  itemBuildingId={itemBuildingId}
                  migvan={migvan}
                  allBoqItems={allBoqItems}
                  invalidCode={item.invalidCode}
                />
                <td>
                  {isBoqFieldEditable(editMode, 'room') ? (
                    <input
                      type="text"
                      value={item.room || ''}
                      onChange={(e) => onUpdate(itemBuildingId, item.id, 'room', e.target.value)}
                      className="boq-input"
                    />
                  ) : (
                    item.room
                  )}
                </td>
                <td>
                  {isBoqFieldEditable(editMode, 'aprt') ? (
                    <input
                      type="text"
                      value={item.aprt || ''}
                      onChange={(e) => onUpdate(itemBuildingId, item.id, 'aprt', e.target.value)}
                      className="boq-input"
                    />
                  ) : (
                    item.aprt
                  )}
                </td>
                <td style={{ width: '50px', maxWidth: '50px', minWidth: '50px', padding: '2px' }}>
                  <input
                    type="text"
                    value={item.unit || ''}
                    readOnly
                    className="boq-input"
                    style={{ 
                      width: '100%', 
                      padding: '1px 2px', 
                      fontSize: '9px',
                      backgroundColor: '#f5f5f5',
                      cursor: 'not-allowed',
                      maxWidth: '50px',
                      minWidth: '50px',
                      textAlign: 'center'
                    }}
                  />
                </td>
                <td>
                  {isBoqFieldEditable(editMode, 'quantity') ? (
                    <input
                      type="number"
                      step={quantityIsWhole ? 1 : 0.01}
                      value={item.quantity || 0}
                      onChange={(e) => {
                        const nextValue = quantityIsWhole
                          ? parseInt(e.target.value, 10) || 0
                          : parseFloat(e.target.value) || 0;
                        onUpdate(itemBuildingId, item.id, 'quantity', nextValue);
                      }}
                      className="boq-input"
                    />
                  ) : (
                    item.quantity
                  )}
                </td>
                <td>
                  {isBoqFieldEditable(editMode, 'wstgPrcnt') ? (
                    <input
                      type="number"
                      step="0.01"
                      value={item.wstgPrcnt || 0}
                      onChange={(e) => onUpdate(itemBuildingId, item.id, 'wstgPrcnt', parseFloat(e.target.value) || 0)}
                      className="boq-input"
                    />
                  ) : (
                    item.wstgPrcnt || 0
                  )}
                </td>
                <td>
                  {isBoqFieldEditable(editMode, 'bufferQty') ? (
                    <input
                      type="number"
                      step="0.01"
                      value={item.bufferQty || 0}
                      onChange={(e) => onUpdate(itemBuildingId, item.id, 'bufferQty', parseFloat(e.target.value) || 0)}
                      className="boq-input"
                    />
                  ) : (
                    item.bufferQty || 0
                  )}
                </td>
                <td>
                  {isBoqFieldEditable(editMode, 'shipDate') ? (
                    <input
                      key={rowKey}
                      type="date"
                      min={minShipDate}
                      value={toDateInputValue(item.shipDate)}
                      onChange={(e) => {
                        const dateValue = e.target.value;
                        if (dateValue) {
                          const [year, month, day] = dateValue.split('-');
                          onUpdate(itemBuildingId, item.id, 'shipDate', `${day}/${month}/${year}`);
                        } else {
                          onUpdate(itemBuildingId, item.id, 'shipDate', '');
                        }
                      }}
                      className="boq-input"
                    />
                  ) : (
                    formatDate(item.shipDate)
                  )}
                </td>
                {editMode === 'full' && (
                  <td>
                    <button
                      className="boq-btn-icon"
                      onClick={() => onDelete(itemBuildingId, item.id)}
                      title="מחק"
                    >
                      🗑️
                    </button>
                  </td>
                )}
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>
      
      <BoqTablePagination
        page={page}
        pageCount={pageCount}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />
    </div>
  );
}
